<template>
	<v-app v-if="rootStore.platformGlobalState === 'Ready' || rootStore.platformGlobalState === 'OnboardingMode'">
		<template v-if="rootStore.platformGlobalState == 'Ready'">
			<v-navigation-drawer
				:width="$vuetify.display.mobile ? 13.75 * rootFontSize : 14.375 * rootFontSize"
				:permanent="!$vuetify.display.mobile"
				v-model="drawer"
				id="navbar"
				color="grey-lighten-5"
				elevation="0"
				:border="false"
				:rail="isRailMode"
				:rail-width="84"
			>
				<template #prepend>
					<div class="logo-container">
						<v-img
							id="logo"
							alt="Platform Logo"
							:src="platformLogo"
							contain
							position="left center"
							transition="scale-transition"
							:width="$vuetify.display.sm ? '32' : ''"
							:max-height="$vuetify.display.sm ? '32' : '40'"
							max-width="150px"
						/>
						<v-btn
							v-if="!$vuetify.display.mobile"
							icon
							variant="outlined"
							class="sidebar-toggle-btn"
							:aria-label="sidebarToggleLabel"
							@click.stop="toggleSidebar"
						>
							<SidePanelOpen20 v-if="sidebarCollapsed" />
							<SidePanelClose20 v-else />
							<v-tooltip
								activator="parent"
								location="end"
							>
								{{ sidebarToggleLabel }}
							</v-tooltip>
						</v-btn>
					</div>
					<v-divider />
				</template>
				<v-list
					nav
					density="compact"
					class="sgnl-pt-16"
				>
					<v-list-item
						v-for="(item, i) in drawerItems"
						:key="i"
						:to="item.to"
						:disabled="item.disabled"
						:data-test="`navbar-item-${item.text.toLowerCase()}`"
						:class="['navbar-item', item.userflowClass]"
						active-class="navbar-item-active"
						@click="$posthog?.capture('sidebar_navigation_clicked', { destination: item.to })"
					>
						<template v-slot:prepend>
							<component
								:is="item.icon"
								class="navbar-icon sgnl-me-neg-16"
							/>
						</template>
						<v-list-item-title>{{ item.text }}</v-list-item-title>
						<v-badge
							v-if="item.to === '/tasks' && activeTaskCount > 0"
							color="primary"
							:content="activeTaskCount"
							inline
							class="sidebar-task-badge"
							:class="{ 'sidebar-task-badge--rail': isRailMode }"
						/>
						<v-tooltip
							v-if="isRailMode"
							activator="parent"
							location="end"
							>{{ item.text }}</v-tooltip
						>
					</v-list-item>
				</v-list>

				<v-spacer />

				<v-list
					nav
					density="compact"
					class="sgnl-pb-16"
				>
					<v-list-item
						class="navbar-item userflow-sidebar-documentation"
						data-test="navbar-item-documentation"
						href="https://docs.signaloid.io/"
						@click.exact.prevent="docsDrawerOpen = !docsDrawerOpen"
					>
						<template v-slot:prepend>
							<Document20 class="navbar-icon sgnl-me-neg-16" />
						</template>
						<v-list-item-title> Documentation </v-list-item-title>
						<template
							v-if="modifierKeyHeld"
							v-slot:append
						>
							<ArrowUpRight16 />
						</template>
						<v-tooltip
							v-if="isRailMode"
							activator="parent"
							location="end"
							>Documentation</v-tooltip
						>
					</v-list-item>
					<v-menu
						v-model="helpMenuOpen"
						location="end top"
						:close-on-content-click="true"
						origin="start top"
						transition="slide-x-transition"
						content-class="help-menu"
					>
						<template v-slot:activator="{ props: menuProps }">
							<v-list-item
								v-if="rootStore.showHelpButton"
								class="navbar-item userflow-sidebar-help"
								data-test="navbar-item-help"
								v-bind="menuProps"
							>
								<template v-slot:prepend>
									<Help20 class="navbar-icon sgnl-me-neg-16" />
								</template>
								<v-list-item-title> Help </v-list-item-title>
								<template v-slot:append>
									<ChevronRight16 class="sgnl-ms-auto sgnl-icon-color-secondary" />
								</template>
								<v-tooltip
									v-if="isRailMode"
									activator="parent"
									location="end"
									>Help</v-tooltip
								>
							</v-list-item>
						</template>
						<v-list
							density="compact"
							class="help-menu-list"
						>
							<v-list-item>
								<v-btn
									variant="text"
									@click="startShortTour"
								>
									<DirectionStraightRight16 class="sgnl-icon-color-secondary" />
									{{ shortTourCompleted ? "Restart QuickStart Tour" : "QuickStart Tour" }}
								</v-btn>
							</v-list-item>
							<v-list-item>
								<v-btn
									variant="text"
									@click="startLongTour"
								>
									<DirectionFork16 class="sgnl-icon-color-secondary" />
									{{ longTourCompleted ? "Restart Detailed Tour" : "Detailed Tour" }}
								</v-btn>
							</v-list-item>
							<v-divider class="help-menu-divider" />
							<v-list-item>
								<v-btn
									variant="text"
									href="mailto:developer-support@signaloid.com"
								>
									<Email16 class="sgnl-icon-color-secondary" />
									Contact us
								</v-btn>
							</v-list-item>
						</v-list>
					</v-menu>
				</v-list>
			</v-navigation-drawer>

			<v-navigation-drawer
				v-model="docsDrawerOpen"
				location="right"
				:width="500"
				:order="-1"
				disable-resize-watcher
				class="docs-drawer"
			>
				<div class="sgnl-flex sgnl-align-center sgnl-justify-space-between sgnl-px-12 docs-drawer-header">
					<span class="sgnl-subtitle-medium sgnl-text-color-default">Documentation</span>
					<div class="docs-drawer-actions">
						<v-btn
							variant="outlined"
							color="secondary"
							href="https://docs.signaloid.io/"
							target="_blank"
						>
							Open in new tab
							<template v-slot:append>
								<ArrowUpRight16 />
							</template>
						</v-btn>
						<v-btn
							icon
							variant="text"
							size="small"
							@click="docsDrawerOpen = false"
						>
							<Close16 />
						</v-btn>
					</div>
				</div>
				<v-divider />
				<iframe
					src="https://docs.signaloid.io/"
					allow="clipboard-write"
					class="docs-drawer-iframe"
				/>
			</v-navigation-drawer>

			<v-app-bar
				id="appbar"
				:height="$vuetify.display.smAndDown ? 57 : 65"
				color="background"
				:border="false"
				:elevation="0"
			>
				<template v-if="$vuetify.display.mobile">
					<v-col
						cols="auto"
						class="sgnl-px-0 sgnl-hidden sgnl-sm-flex"
					>
						<v-img
							id="logo"
							alt="Platform Logo"
							contain
							:src="platformLogo"
							transition="scale-transition"
							width="35"
							max-height="35"
						/>
					</v-col>
					<v-app-bar-nav-icon
						class="userflow-sidebar-toggle hamburger-btn"
						@click="drawer = !drawer"
					/>
				</template>
				<div
					v-if="$route.meta.title"
					class="app-bar-title sgnl-h6-semibold sgnl-text-color-default"
				>
					{{ $route.meta.title }}
				</div>
				<v-spacer />
				<TopNav @sign-out="requestSignOut" />
			</v-app-bar>
		</template>

		<v-main>
			<router-view />
		</v-main>

		<!-- Global components -->
		<TierUpgradeHintDialog
			:triggeredLimit="exceededTierLimitsForHintDialog[0]"
			v-model:show-dialog="showLimitHintDialog"
		/>
		<v-snackbar-queue
			v-model="transientFeedbackQueue"
			location="top center"
		>
			<template #text="{ item }">
				<div class="snackbar-content">
					<div
						:class="['snackbar-content__icon', 'snackbar-icon-container', `snackbar-${item.feedbackType}`]"
					>
						<ErrorFilled16 v-if="item.feedbackType === 'error'" />
						<InformationFilled16 v-else-if="item.feedbackType === 'info'" />
						<CheckmarkFilled16 v-else />
					</div>
					<span class="snackbar-content__text">{{ item.text }}</span>
				</div>
			</template>
			<template #actions="{ props: actionProps }">
				<v-btn
					variant="text"
					size="small"
					icon
					@click="actionProps.onClick"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar-queue>
		<GuidedTour />
		<UploadTray />
		<v-dialog
			v-model="showSignOutUploadWarning"
			:width="$vuetify.display.mobile ? '95%' : '440px'"
		>
			<v-card>
				<v-card-title class="dialog-header sgnl-subtitle-medium">Uploads still in progress</v-card-title>
				<v-divider />
				<v-card-text class="dialog-body sgnl-paragraph-s-regular">
					Signing out now will cancel the uploads that have not finished, and they cannot be resumed.
				</v-card-text>
				<v-divider />
				<v-card-actions class="dialog-footer">
					<v-btn
						variant="outlined"
						@click="showSignOutUploadWarning = false"
						>Keep uploading</v-btn
					>
					<v-btn
						variant="flat"
						color="danger"
						@click="confirmSignOutWithActiveUploads"
						>Sign out anyway</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-app>
	<v-app v-else>
		<v-overlay
			:model-value="true"
			scrim="white"
			class="sgnl-align-center sgnl-justify-center"
		>
			<template v-if="fatalErrorInitialising">
				<v-card
					:width="$vuetify.display.mobile ? '95%' : '500px'"
					color="white"
				>
					<v-alert type="error">
						An error occurred while loading your account details. Please refresh the page and try again.
						{{ util.ContactSignaloidSupportMessage }}
					</v-alert>
				</v-card>
			</template>
			<v-progress-circular
				v-else
				color="accent"
				size="64"
			/>
		</v-overlay>
	</v-app>
</template>

<script lang="ts">
import { defineComponent, markRaw } from "vue";

// Libraries
import { signOut } from "aws-amplify/auth";

// Utilities
import * as util from "@/js/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";
import codeLanguagesList from "@/assets/_codeLanguagesList.json";

// Carbon Icons
import Dashboard20 from "@carbon/icons-vue/es/dashboard/20";
import Code20 from "@carbon/icons-vue/es/code/20";
import Chip20 from "@carbon/icons-vue/es/chip/20";
import ConnectSource20 from "@carbon/icons-vue/es/connect--source/20";
import LogoGithub20 from "@carbon/icons-vue/es/logo--github/20";
import Meter20 from "@carbon/icons-vue/es/meter/20";
import Apps20 from "@carbon/icons-vue/es/apps/20";
import TaskView20 from "@carbon/icons-vue/es/task--view/20";
import ChartRing20 from "@carbon/icons-vue/es/chart--ring/20";
import Purchase20 from "@carbon/icons-vue/es/purchase/20";
import Settings20 from "@carbon/icons-vue/es/settings/20";
import Help20 from "@carbon/icons-vue/es/help/20";
import Document20 from "@carbon/icons-vue/es/document/20";
import ChevronRight16 from "@carbon/icons-vue/es/chevron--right/16";
import ArrowUpRight16 from "@carbon/icons-vue/es/arrow--up-right/16";
import Close16 from "@carbon/icons-vue/es/close/16";
import DirectionStraightRight16 from "@carbon/icons-vue/es/direction--straight--right/16";
import DirectionFork16 from "@carbon/icons-vue/es/direction--fork/16";
import Email16 from "@carbon/icons-vue/es/email/16";
import SidePanelOpen20 from "@carbon/icons-vue/es/side-panel--open/20";
import SidePanelClose20 from "@carbon/icons-vue/es/side-panel--close/20";

// Components
import TopNav from "@/components/TopNav.vue";
import TierUpgradeHintDialog from "@/components/Common/TierUpgradeHintDialog.vue";
import GuidedTour from "@/components/GuidedTour/GuidedTour.vue";
import UploadTray from "@/components/DataSources/UploadTray.vue";
import { useTour } from "@/composables/useTour";
import { shortTourSteps, SHORT_TOUR_ID, longTourSteps, LONG_TOUR_ID } from "@/composables/tourDefinitions";
import { useFeedbackQueue } from "@/composables/feedbackQueue";
import ErrorFilled16 from "@carbon/icons-vue/es/error--filled/16";
import InformationFilled16 from "@carbon/icons-vue/es/information--filled/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";

// Stores
import { mapState } from "pinia";
import { useDataSourcesStore } from "@/stores/dataSources";
import { useTasksStore } from "@/stores/tasks";
import { useUserStore } from "@/stores/user";
import { useCoresStore } from "@/stores/cores";
import { useEditorStore } from "@/stores/editor";
import { useRepositoriesStore } from "@/stores/repositories";
import { useGithubStore } from "@/stores/github";
import { useRootStore } from "@/stores/root";
import { usePipelinesStore } from "@/stores/pipelines";
import { identifyAnalyticsUser } from "./plugins/identify";
import { TierLimitBusEvent, TierLimitEventTypeE } from "./eventBus/tierLimitEventBus";
import { UserLimits } from "./js/tierconfig";
import { useOutputsStore } from "./stores/outputs";
import { useBuildsStore } from "./stores/builds";
import { useUploadsStore } from "@/stores/uploads";

type ComponentData = {
	drawer: boolean;
	sidebarCollapsed: boolean;
	drawerItems: DrawerItem[];
	auth: {
		user: undefined | string;
		state: undefined | string;
		data: undefined | any;
	};
	tierLimitEventBusSub: undefined | Function;
	activeReconcileTimer: undefined | ReturnType<typeof setInterval>;
	activeReconcileOnVisibility: undefined | (() => void);
	exceededTierLimitsForHintDialog: UserLimits[];
	showLimitHintDialog: boolean;
	fatalErrorInitialising: boolean;
	docsDrawerOpen: boolean;
	modifierKeyHeld: boolean;
	rootFontSize: number;
	helpMenuOpen: boolean;
	showSignOutUploadWarning: boolean;
};

type DrawerItem = {
	text: string;
	to: string;
	icon: object;
	disabled: boolean;
	userflowClass?: string;
};

export default defineComponent({
	name: "App",
	components: {
		TopNav,
		TierUpgradeHintDialog,
		GuidedTour,
		UploadTray,
		ErrorFilled16,
		InformationFilled16,
		CheckmarkFilled16,
		Help20,
		Document20,
		ChevronRight16,
		ArrowUpRight16,
		Close16,
		DirectionStraightRight16,
		DirectionFork16,
		Email16,
		SidePanelOpen20,
		SidePanelClose20,
	},
	data: (): ComponentData => ({
		drawer: true,
		sidebarCollapsed: util.safeGetLocalStorage<boolean>("signaloid-sidebar-collapsed", false),
		drawerItems: [
			{
				text: "Home",
				to: "/home",
				icon: markRaw(Dashboard20),
				disabled: false,
				userflowClass: "userflow-sidebar-home",
			},
			{
				text: "Code Playground",
				to: "/launch",
				icon: markRaw(Code20),
				disabled: false,
				userflowClass: "userflow-sidebar-code-playground",
			},
			{
				text: "Cores",
				to: "/cores",
				icon: markRaw(Chip20),
				disabled: false,
				userflowClass: "userflow-sidebar-cores",
			},
			{
				text: "Data and Sensors",
				to: "/datasources",
				icon: markRaw(ConnectSource20),
				disabled: false,
				userflowClass: "userflow-sidebar-data-and-sensors",
			},
			{
				text: "Repositories",
				to: "/repositories",
				icon: markRaw(LogoGithub20),
				disabled: false,
				userflowClass: "userflow-sidebar-repositories",
			},
			// Disabled till api migration for pipelines is done
			//  { text: "Pipelines", to: "/pipelines", icon: markRaw(PipelineIcon), disabled: false },
			{
				text: "Benchmarking",
				to: "/benchmarking",
				icon: markRaw(Meter20),
				disabled: false,
				userflowClass: "userflow-sidebar-benchmarks",
			},
			{
				text: "API App Highlights",
				to: "/apps",
				icon: markRaw(Apps20),
				disabled: false,
				userflowClass: "userflow-sidebar-api-app-highlights",
			},
			{
				text: "Builds and Tasks",
				to: "/tasks",
				icon: markRaw(TaskView20),
				disabled: false,
				userflowClass: "userflow-sidebar-builds-and-tasks",
			},
			{
				text: "Usage",
				to: "/usage",
				icon: markRaw(ChartRing20),
				disabled: false,
				userflowClass: "userflow-sidebar-usage",
			},
			{
				text: "Billing",
				to: "/billing",
				icon: markRaw(Purchase20),
				disabled: false,
				userflowClass: "userflow-sidebar-billing",
			},
			{
				text: "Settings",
				to: "/settings",
				icon: markRaw(Settings20),
				disabled: false,
				userflowClass: "userflow-sidebar-settings",
			},
		],
		auth: {
			user: undefined,
			state: undefined,
			data: undefined,
		},
		tierLimitEventBusSub: undefined,
		activeReconcileTimer: undefined,
		activeReconcileOnVisibility: undefined,
		exceededTierLimitsForHintDialog: [],
		showLimitHintDialog: false,
		fatalErrorInitialising: false,
		docsDrawerOpen: false,
		modifierKeyHeld: false,
		rootFontSize: parseFloat(getComputedStyle(document.documentElement).fontSize),
		helpMenuOpen: false,
		showSignOutUploadWarning: false,
	}),
	computed: {
		drawerActiveItem(): number | null {
			const routeDrawerIndex = this.drawerItems.findIndex((element) => this.$route.path.startsWith(element.to));
			return routeDrawerIndex != -1 ? routeDrawerIndex : null;
		},
		...mapState(useRootStore, { platformLogo: "platformLogo" }),
		isMobileViewport(): boolean {
			return this.$vuetify.display.xs;
		},
		versionHash(): string {
			return import.meta.env.VITE_COMMIT_HASH ? import.meta.env.VITE_COMMIT_HASH.slice(0, 7) : "-";
		},
		shortTourCompleted(): boolean {
			return !!this.userStore.currentUserObjectFromDB?.OnboardingState?.Tour_QuickStart_Completed;
		},
		longTourCompleted(): boolean {
			return !!this.userStore.currentUserObjectFromDB?.OnboardingState?.Tour_Detailed_Completed;
		},
		isRailMode(): boolean {
			return this.sidebarCollapsed && !this.$vuetify.display.mobile;
		},
		sidebarToggleLabel(): string {
			return this.sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar";
		},
		activeTaskCount(): number {
			return (this.tasksStore.activeTasks?.length ?? 0) + (this.buildStore.activeBuilds?.length ?? 0);
		},
	},
	watch: {
		sidebarCollapsed(newVal: boolean) {
			util.safeSetLocalStorage("signaloid-sidebar-collapsed", newVal);
		},
		"$vuetify.display.mobile"(isMobile: boolean) {
			// Close the docs panel when entering mobile; disable-resize-watcher stops it reopening on the way back.
			if (isMobile) this.docsDrawerOpen = false;
		},
	},
	methods: {
		toggleSidebar() {
			this.sidebarCollapsed = !this.sidebarCollapsed;
		},
		ensureSidebarOpenForTour() {
			if (this.$vuetify.display.mobile) this.drawer = false;
			else this.sidebarCollapsed = false;
		},
		startShortTour() {
			this.ensureSidebarOpenForTour();
			this.startTour(SHORT_TOUR_ID, shortTourSteps);
		},
		startLongTour() {
			this.ensureSidebarOpenForTour();
			this.startTour(LONG_TOUR_ID, longTourSteps);
		},
		onModifierKey(event: KeyboardEvent) {
			this.modifierKeyHeld = event.ctrlKey || event.metaKey;
		},
		onWindowBlur() {
			this.modifierKeyHeld = false;
		},
		logStartupInfo() {
			const version = import.meta.env.VITE_VERSION;
			queueMicrotask(
				console.log.bind(
					console,
					`Signaloid Cloud Developer Platform (v${version}-${this.versionHash})\nCopyright 2024 Signaloid`,
				),
			);
		},
		// User-initiated sign-out. Warn first if background uploads would be cancelled; the
		// involuntary session-expiry path calls awsSignOut() directly and skips this.
		requestSignOut() {
			if (this.uploadsStore.hasActive) {
				this.showSignOutUploadWarning = true;
				return;
			}
			this.awsSignOut();
		},
		confirmSignOutWithActiveUploads() {
			this.showSignOutUploadWarning = false;
			this.awsSignOut();
		},
		async awsSignOut() {
			// We are leaving for /login regardless, so silence the tray's beforeunload guard.
			this.uploadsStore.bypassUnloadGuard = true;
			try {
				this.buildStore.teardownRealtime();
				this.tasksStore.teardownRealtime();
				await signOut();
				util.clearLocalState();
				const taskOutputsStore = useOutputsStore();
				await taskOutputsStore.purgeCache();
				window.location.href = "/login";
			} catch (error) {
				monitoringCaptureError(error, "User sign out");
				util.clearLocalState();
				window.location.href = "/login";
			}
		},
		async tierLimitEventBusCallback(event: TierLimitBusEvent) {
			// This function will handle global tier limit events and show the dialog if needed
			if (event.type === TierLimitEventTypeE.UsageChanged) {
				// this might be expensive, but necessary until we have web socket setup
				this.userStore.fetchCurrentUserObjectFromDB();
			} else if (event.type === TierLimitEventTypeE.LimitExceeded) {
				// check if the dialog is already visible
				if (this.showLimitHintDialog !== true) {
					this.exceededTierLimitsForHintDialog = event.affectedLimits;
					this.showLimitHintDialog = true;
				}

				event.affectedLimits.map((affectedLimit) => {
					// @ts-ignore
					this.$posthog?.capture("user_hit_tier_limit", {
						affectedLimits: affectedLimit,
						current_tier: this.userStore?.currentUserSubscriptions?.Tier,
						page: this.$route.path,
					});
				});
			}
		},
		subscribeToTierLimitEventBus() {
			this.tierLimitEventBusSub = this.rootStore.tierLimitEventBus.on(this.tierLimitEventBusCallback);
		},
		async maybeStartOnboardingTour() {
			try {
				const onboardingState = await this.userStore.getCurrentUserOnboardingState();
				if (!onboardingState?.Tour_QuickStart_Completed) {
					this.startTour(SHORT_TOUR_ID, shortTourSteps);
				}
			} catch {
				/* If onboarding state fetch fails, skip the tour silently */
			}
		},
	},
	setup() {
		// Note: gets called after beforeCreate in vue2
		const dataSourcesStore = useDataSourcesStore();
		const userStore = useUserStore();
		const coresStore = useCoresStore();
		const editorStore = useEditorStore();
		const repositoriesStore = useRepositoriesStore();
		const githubStore = useGithubStore();
		const rootStore = useRootStore();
		const tasksStore = useTasksStore();
		const buildStore = useBuildsStore();
		const uploadsStore = useUploadsStore();

		const { transientFeedbackQueue } = useFeedbackQueue();
		const { start: startTour } = useTour();

		return {
			dataSourcesStore,
			userStore,
			coresStore,
			editorStore,
			repositoriesStore,
			githubStore,
			rootStore,
			tasksStore,
			buildStore,
			uploadsStore,
			util,
			transientFeedbackQueue,
			startTour,
		};
	},
	beforeCreate() {
		//
		// console.log("And there was light");
		/*
		 *	WARNING: Do not change the order of the three following function calls below!
		 */
		const rootStore = useRootStore();
		rootStore.setCodeLanguagesList(codeLanguagesList);
		// // set auth head if gh authenticated
		// const githubStore = useGithubStore();
		// githubStore.setupClientAndGetUser();
		const pipelinesStore = usePipelinesStore();
		pipelinesStore.initialisePipelinesFromLocalStorage();
	},

	async created() {
		this.logStartupInfo();
		this.rootStore.setupListenerForOnlineStatus();

		try {
			// Get defaults of the authenticated user from AWS and DB
			// Note: fetching this details from the user store via this.userStore.fetchCurrentUserFromAWS();
			// put kea-web into a login loop when you are not logged in ????
			// await Auth.currentAuthenticatedUser();
			await this.userStore.fetchCurrentUserFromAWS();
		} catch {
			// User is not authenticated. Clear local state and redirect to login
			util.clearLocalState();
			window.location.href = "/login";
			return;
		}

		const session = await this.userStore.fetchCurrentUserSessionFromAWS();
		if (!session) {
			// User is not logged in - redirect to login
			await this.awsSignOut();
			window.location.href = "/login";
			return;
		}

		try {
			/*
			 *	identifyAnalyticsUser can except but it makes the same call as the two try-catch blocks above it. If
			 *	these succeeded then identifyAnalyticsUser has great probability to succeed. A workaround would be to
			 *	pass information along and not re-call the underlying exception-possible functions.
			 */
			await identifyAnalyticsUser();
		} catch {
			/*
			 *	We shouldn't stop the App from running if this failed. But we can set-up a retry.
			 */
			setTimeout(identifyAnalyticsUser, 500 /* ms */);
		}

		try {
			// If any of these requests fail, we cannot continue
			// TODO: Why not?
			await this.userStore.fetchCurrentUserSubscriptions();
		} catch (error) {
			this.fatalErrorInitialising = true;
			console.log("fetchCurrentUserSubscriptions exception:");
			console.log(error);
		}

		util.waitFor(() => this.rootStore.platformGlobalState === "InitialisingUserAuth").then(() => {
			this.userStore.fetchCurrentUserObjectFromDB();
		});

		util.waitFor(() => this.rootStore.platformGlobalState === "Ready").then(async () => {
			this.rootStore.showHelpButton = true;

			/*
			 *	Initialise user customisations
			 */
			this.userStore.fetchCurrentUserCustomizationsFromDB().then(() => {
				this.rootStore.updatePlatformLogo();
				this.rootStore.updatePlatformApplicationHighlights();
				this.rootStore.updateComputeEngineHost();
			});

			// Initialise the Editor
			this.editorStore.initialiseEditorStateFromRemote();

			// Set up the GitHub client
			this.githubStore.setupClientAndGetUser();

			// Initialise cores, data sources, and tasks
			this.coresStore.initialiseExecutionCoresFromRemote();
			this.dataSourcesStore.initialiseDataSourcesFromRemote();
			this.tasksStore.initialiseTaskHistory();
			this.buildStore.initialiseBuildHistory();

			// * Subscribe to realtime build/task status updates via SDK WebSocket
			this.userStore.getCurrentUserID().then((userId) => {
				if (userId) {
					this.buildStore.initRealtime(userId);
					this.tasksStore.initRealtime(userId);
				}
			});

			// Live status comes from the realtime WS, but the WS is best-effort:
			// per-connection, no replay across reconnects, once-only delivery, and
			// it doesn't re-subscribe after a drop. So the single source of truth
			// for the active lists is this periodic reconcile against the API.
			// Poll only while the tab is visible and something is active; reconcile
			// immediately when the tab regains focus (to catch up on what the WS
			// dropped while hidden).
			const ACTIVE_RECONCILE_INTERVAL_MS = 5000;
			const reconcileActive = () => {
				if (document.visibilityState === "hidden") return;
				if (this.buildStore.activeBuilds && this.buildStore.activeBuilds.length > 0) {
					this.buildStore.reconcileActiveBuilds();
				}
				if (this.tasksStore.activeTasks && this.tasksStore.activeTasks.length > 0) {
					this.tasksStore.reconcileActiveTasks();
				}
			};
			this.activeReconcileTimer = setInterval(reconcileActive, ACTIVE_RECONCILE_INTERVAL_MS);
			this.activeReconcileOnVisibility = () => {
				if (document.visibilityState === "visible") reconcileActive();
			};
			document.addEventListener("visibilitychange", this.activeReconcileOnVisibility);

			this.subscribeToTierLimitEventBus();
			this.maybeStartOnboardingTour();
		});
	},
	mounted() {
		if (this.$vuetify.display.mobile) {
			this.drawer = false;
		}
		window.addEventListener("keydown", this.onModifierKey);
		window.addEventListener("keyup", this.onModifierKey);
		window.addEventListener("blur", this.onWindowBlur);
	},
	beforeUnmount() {
		window.removeEventListener("keydown", this.onModifierKey);
		window.removeEventListener("keyup", this.onModifierKey);
		window.removeEventListener("blur", this.onWindowBlur);
		// return onAuthUIStateChange;
		if (this.tierLimitEventBusSub !== undefined) {
			this.tierLimitEventBusSub();
		}
		if (this.activeReconcileTimer !== undefined) {
			clearInterval(this.activeReconcileTimer);
			this.activeReconcileTimer = undefined;
		}
		if (this.activeReconcileOnVisibility !== undefined) {
			document.removeEventListener("visibilitychange", this.activeReconcileOnVisibility);
			this.activeReconcileOnVisibility = undefined;
		}
		this.buildStore.teardownRealtime();
		this.tasksStore.teardownRealtime();
	},
});
</script>

<style>
#app {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	color: var(--sgnl-default-primary-text);
}

/* #appbar {
    -moz-box-shadow:    0px -1px 10px 6px #ccc;
    -webkit-box-shadow: 0px -1px 10px 6px #ccc;
    box-shadow:         0px -1px 10px 6px #ccc;
}

#navbar {
    -moz-box-shadow:    inset 0 11px 10px -6px #ccc;
    -webkit-box-shadow: inset 0 11px 10px -6px #ccc;
    box-shadow:         inset 0 11px 10px -6px #ccc;
} */

#navbar {
	border-right: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	transition-duration: var(--sgnl-motion-duration-moderate-01);
	transition-timing-function: var(--sgnl-expressive-effect-standard);
}

#navbar > .v-navigation-drawer__prepend {
	overflow: visible;
}

#appbar {
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
}

/* Navbar styling */
#navbar > .v-navigation-drawer__content {
	display: flex;
	flex-direction: column;
}

#navbar a {
	color: inherit;
	text-decoration: none;
}

#navbar .v-list--nav {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
}

/* Vuetify injects margin-top:4px on non-first nav items; in a flex list it no
   longer collapses, so it stacks on the gap. Zero it — the gap owns the spacing. */
#navbar .v-list--nav .v-list-item {
	margin-block-start: 0;
}

/* Desktop inset for nav items (was a per-item sgnl-mx-md-8); on the list so the
   84px rail keeps the icon centered. Base stays at Vuetify's 8px nav padding. */
@media (min-width: 960px) {
	#navbar .v-list--nav {
		padding-inline: var(--sgnl-space-16);
	}
}

.navbar-item {
	border-radius: var(--sgnl-border-radius-6);
	color: var(--sgnl-default-secondary-text);
	position: relative;
}

.sidebar-task-badge {
	position: absolute;
	right: 0;
	top: 50%;
	transform: translateY(-50%);
	pointer-events: none;
}

.sidebar-task-badge--rail {
	top: 0;
	right: 0;
	transform: translate(25%, -25%);
}

.navbar-item .v-list-item-title {
	color: var(--sgnl-default-secondary-text);
}

.navbar-item .v-list-item__prepend > .navbar-icon ~ .v-list-item__spacer {
	width: 32px;
}

.navbar-icon {
	display: inline-block;
	color: var(--sgnl-default-secondary-background-mono-inverse);
	flex-shrink: 0;
}

.navbar-item:hover {
	background-color: var(--sgnl-default-background-hover);
}

.navbar-item:hover .v-list-item-title {
	text-decoration: underline;
}

.navbar-item-active {
	background-color: var(--sgnl-brand-background-inverse);
}

.navbar-item-active .v-list-item__overlay {
	opacity: 0;
}

.navbar-item-active:hover {
	background-color: var(--sgnl-brand-background-inverse);
}

.navbar-item-active .v-list-item__prepend,
.navbar-item-active .navbar-icon {
	color: var(--sgnl-brand-icon);
}

.navbar-item-active .v-list-item__content,
.navbar-item-active .v-list-item-title {
	color: var(--sgnl-brand-text);
	text-decoration: underline;
}

/* Logo container */
.logo-container {
	position: relative;
	display: flex;
	align-items: center;
	height: 65px;
	padding-inline-start: var(--sgnl-space-24);
	padding-inline-end: var(--sgnl-space-16);
	overflow: visible;
	transition: padding var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-standard);
}

@media (max-width: 959px) {
	.logo-container {
		height: 57px;
		padding-inline-start: var(--sgnl-space-16);
	}
}

/* Sidebar toggle button */
.logo-container .sidebar-toggle-btn.v-btn {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	right: calc(-1 * var(--sgnl-unit-18));
	z-index: 1;
	padding: var(--sgnl-space-4);
	width: var(--sgnl-unit-32);
	height: var(--sgnl-unit-32);
	color: var(--sgnl-default-secondary-icon);
	border-color: var(--sgnl-default-primary-border);
	background-color: var(--sgnl-default-primary-background);
}

/* Rail mode overrides — explicitly hide text since overflow:visible on root prevents clipping */
#navbar.v-navigation-drawer--rail .v-list-item-title,
#navbar.v-navigation-drawer--rail .v-list-item__spacer,
#navbar.v-navigation-drawer--rail .v-list-item__append {
	display: none;
}

#navbar.v-navigation-drawer--rail .navbar-icon {
	margin-inline-end: 0;
}

#navbar.v-navigation-drawer--rail .navbar-item:hover {
	box-shadow: 0 0 0 var(--sgnl-border-width-2) var(--sgnl-focus-ring-secondary);
}

#navbar.v-navigation-drawer--rail .navbar-item-active {
	outline: var(--sgnl-border-width-1) solid var(--sgnl-brand-border);
}

#navbar.v-navigation-drawer--rail .logo-container {
	padding-inline-end: var(--sgnl-space-8);
}

#logo {
	transition:
		width var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-standard),
		max-width var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-standard),
		max-height var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-standard);
}

#navbar.v-navigation-drawer--rail #logo {
	width: 28px;
	max-height: 28px !important;
	max-width: 28px !important;
}

.app-bar-title {
	padding-left: var(--sgnl-space-32);
	white-space: nowrap;
}

.hamburger-btn {
	max-width: 44px;
}

@media (max-width: 959px) {
	#appbar > .v-toolbar__content {
		padding-left: var(--sgnl-space-16);
		padding-right: var(--sgnl-space-16);
	}

	.app-bar-title {
		margin-left: 0;
	}
}

.help-menu-list {
	padding: var(--sgnl-space-4) 0;
}

.help-menu-list .help-menu-divider {
	border-color: var(--sgnl-default-primary-border);
	opacity: 1;
}

.help-menu-list .v-btn {
	display: flex;
	height: 32px;
	padding: var(--sgnl-space-8) 0;
	justify-content: flex-start;
	align-items: center;
	gap: var(--sgnl-space-8);
	background: transparent;
	border: none;
	box-shadow: none;
	width: 100%;
}

.help-menu-list .v-btn .v-btn__content {
	color: var(--sgnl-default-secondary-text);
	text-align: center;
	font-size: var(--sgnl-font-size-paragraph-s);
	font-style: normal;
	font-weight: 500;
	line-height: var(--sgnl-font-line-height-paragraph-s);
	letter-spacing: var(--sgnl-font-letter-spacing-0);
	gap: var(--sgnl-space-8);
}

.help-menu-list .v-btn:hover {
	background: transparent;
}

.help-menu-list .v-btn .v-btn__overlay,
.help-menu-list .v-btn .v-btn__underlay {
	display: none;
}

.help-menu-list .v-btn:hover .v-btn__content {
	text-decoration: underline;
}
</style>

<style scoped>
.v-list-item__title {
	text-align: left;
}

.userflow-sidebar-documentation :deep(.v-list-item__append) {
	min-inline-size: var(--sgnl-icon-16);
}

.docs-drawer :deep(.v-navigation-drawer__content) {
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.docs-drawer-header {
	height: 65px;
	flex-shrink: 0;
}

.docs-drawer-actions {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-16);
}

.docs-drawer-iframe {
	width: 100%;
	flex: 1;
	border: none;
}

@media (max-width: 959px) {
	.docs-drawer-header {
		height: 57px;
	}
}
</style>

<style lang="scss">
[textarea-narrowLineHeight] {
	line-height: 1.5;
}
[code-area] {
	font-family: var(--sgnl-font-family-mono);
	font-size: 0.85rem;
	background-color: var(--sgnl-default-secondary-background-mono);
}
</style>
